import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, List, ListItem, ListItemText, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks";
import { pluginService } from "../services/PluginService";

export const PluginInfoPage: React.FC = () => {
  const [plugins, setPlugins] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: { events: boolean; sources: boolean; actions: boolean } }>({});

  // Get user authentication and role access
  const { user } = useAuth();

  const fetchPluginInfo = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await pluginService.getPluginInfo();
      setPlugins(response.plugins || []);
    } catch (err) {
      setError("Failed to fetch plugin information.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPluginInfo();
  }, []);

  const toggleExpanded = (pluginName: string, section: "events" | "sources" | "actions") => {
    setExpandedSections((prev) => ({
      ...prev,
      [pluginName]: {
        ...prev[pluginName],
        [section]: !prev[pluginName]?.[section],
      },
    }));
  };

  const isExpanded = (pluginName: string, section: "events" | "sources" | "actions") => {
    return expandedSections[pluginName]?.[section] || false;
  };

  const renderParameter = (paramName: string, param: any) => (
    <Box key={paramName} sx={{ mb: 1 }}>
      <Typography variant="body2" fontWeight="bold">
        {paramName} ({param.type}){param.required && <Chip label="Required" size="small" color="primary" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
        {param.description}
      </Typography>
      {param.enum && (
        <Box sx={{ ml: 2, mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Allowed values:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
            {param.enum.map((value: string) => (
              <Chip key={value} label={value} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}
      {param.min !== undefined && param.max !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
          Range: {param.min} - {param.max}
        </Typography>
      )}
    </Box>
  );

  const renderPlugin = (plugin: any, index: number) => (
    <Box key={plugin.name} sx={{ width: { xs: "100%", md: "50%", lg: "33.333%" }, p: 1 }}>
      <Box sx={{ height: "100%" }}>
        <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <CardContent sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom>
              {plugin.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {plugin.name === "GooglePlugin"
                ? "Plugin that integrates with Google Calendar to detect events and provide actions."
                : plugin.name === "WebSearchPlugin"
                  ? "Plugin that provides web search capabilities for agents to find information online."
                  : "Plugin that provides various integrations and capabilities."}
            </Typography>

            {/* Summary chips */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
              {plugin.events && plugin.events.length > 0 && <Chip label={`${plugin.events.length} Events`} size="small" color="primary" variant="outlined" />}
              {plugin.sources && plugin.sources.length > 0 && <Chip label={`${plugin.sources.length} Sources`} size="small" color="secondary" variant="outlined" />}
              {plugin.actions && plugin.actions.length > 0 && <Chip label={`${plugin.actions.length} Actions`} size="small" color="success" variant="outlined" />}
            </Box>

            {/* Events Section */}
            {plugin.events && plugin.events.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Events ({plugin.events.length})
                </Typography>
                <List dense>
                  {plugin.events.slice(0, isExpanded(plugin.name, "events") ? undefined : 3).map((event: any) => (
                    <ListItem key={event.type} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">{event.type}</Typography>
                            {event.category && <Chip label={event.category} size="small" color="primary" sx={{ fontSize: "0.7rem" }} />}
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {event.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {plugin.events.length > 3 && (
                  <Button size="small" onClick={() => toggleExpanded(plugin.name, "events")} sx={{ mt: 1, p: 0, minWidth: "auto", textTransform: "none" }}>
                    {isExpanded(plugin.name, "events") ? (
                      <>
                        <ExpandLessIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
                        Show less
                      </>
                    ) : (
                      <>
                        <ExpandMoreIcon sx={{ fontSize: "1rem", mr: 0.5 }} />+{plugin.events.length - 3} more events
                      </>
                    )}
                  </Button>
                )}
              </Box>
            )}

            {/* Sources Section */}
            {plugin.sources && plugin.sources.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sources ({plugin.sources.length})
                </Typography>
                <List dense>
                  {plugin.sources.slice(0, isExpanded(plugin.name, "sources") ? undefined : 3).map((source: any) => (
                    <ListItem key={source.id} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={<Typography variant="body2">{source.name}</Typography>}
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {source.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {plugin.sources.length > 3 && (
                  <Button size="small" onClick={() => toggleExpanded(plugin.name, "sources")} sx={{ mt: 1, p: 0, minWidth: "auto", textTransform: "none" }}>
                    {isExpanded(plugin.name, "sources") ? (
                      <>
                        <ExpandLessIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
                        Show less
                      </>
                    ) : (
                      <>
                        <ExpandMoreIcon sx={{ fontSize: "1rem", mr: 0.5 }} />+{plugin.sources.length - 3} more sources
                      </>
                    )}
                  </Button>
                )}
              </Box>
            )}

            {/* Actions Section */}
            {plugin.actions && plugin.actions.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Actions ({plugin.actions.length})
                </Typography>
                <List dense>
                  {plugin.actions.slice(0, isExpanded(plugin.name, "actions") ? undefined : 3).map((action: any) => (
                    <ListItem key={action.id} sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">{action.name}</Typography>
                            <Chip label={action.type} size="small" color="secondary" sx={{ fontSize: "0.7rem" }} />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {action.description}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                {plugin.actions.length > 3 && (
                  <Button size="small" onClick={() => toggleExpanded(plugin.name, "actions")} sx={{ mt: 1, p: 0, minWidth: "auto", textTransform: "none" }}>
                    {isExpanded(plugin.name, "actions") ? (
                      <>
                        <ExpandLessIcon sx={{ fontSize: "1rem", mr: 0.5 }} />
                        Show less
                      </>
                    ) : (
                      <>
                        <ExpandMoreIcon sx={{ fontSize: "1rem", mr: 0.5 }} />+{plugin.actions.length - 3} more actions
                      </>
                    )}
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Plugin Information
      </Typography>

      {isLoading && <CircularProgress sx={{ display: "block", my: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isLoading && plugins.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No plugins found.
        </Alert>
      )}

      {plugins.length > 0 && <Box sx={{ display: "flex", flexWrap: "wrap", mx: -1 }}>{plugins.map((plugin, index) => renderPlugin(plugin, index))}</Box>}
    </Box>
  );
};
